import { useEffect } from 'react';

/**
 * Runs `reset` whenever the user RETURNS to this page after a bank handoff —
 * used to release a CTA loading flag that's intentionally left true through a
 * full-page navigation to the bank.
 *
 * The bank handoffs are CROSS-ORIGIN (form POST / window.location.assign), which
 * makes the page NOT bfcache-eligible — so Back does a hard reload and `pageshow`
 * fires with persisted=FALSE. Gating on `persisted` therefore never fired and
 * the spinner stayed stuck (and, because the loading CTA is disabled, the next
 * click was swallowed too). So reset on ANY return-to-page signal: pageshow
 * (any), window focus, and visibilitychange→visible. `reset` should be
 * idempotent — firing on several of these is harmless.
 *
 * `reset` is read from a ref so callers can pass an inline arrow without
 * re-subscribing the listeners every render.
 */
export function useResetOnReturn(reset: () => void): void {
	// biome-ignore lint/correctness/useExhaustiveDependencies: reset is expected to be a stable setState dispatcher; subscribe the return-listeners once on mount.
	useEffect(() => {
		const onReset = () => reset();
		const onVisible = () => {
			if (document.visibilityState === 'visible') onReset();
		};
		window.addEventListener('pageshow', onReset);
		window.addEventListener('focus', onReset);
		document.addEventListener('visibilitychange', onVisible);
		return () => {
			window.removeEventListener('pageshow', onReset);
			window.removeEventListener('focus', onReset);
			document.removeEventListener('visibilitychange', onVisible);
		};
	}, []);
}
